// Regras do nó "Condição" (SE/SENÃO): campos, operadores e rótulos de ramo.
// O backend (Talkvia::FunnelConditionEvaluator) avalia as mesmas chaves.

export const CONDITION_OPERATORS = [
  { value: 'equals', label: 'é igual a', needsValue: true },
  { value: 'not_equals', label: 'é diferente de', needsValue: true },
  { value: 'contains', label: 'contém', needsValue: true },
  { value: 'not_contains', label: 'não contém', needsValue: true },
  { value: 'starts_with', label: 'começa com', needsValue: true },
  { value: 'greater_than', label: 'é maior que', needsValue: true },
  { value: 'less_than', label: 'é menor que', needsValue: true },
  { value: 'exists', label: 'existe', needsValue: false },
  { value: 'not_exists', label: 'não existe', needsValue: false },
  { value: 'empty', label: 'está vazio', needsValue: false },
  { value: 'not_empty', label: 'não está vazio', needsValue: false },
];

export const CONDITION_MATCH_OPTIONS = [
  { value: 'all', label: 'Todas as regras (E)' },
  { value: 'any', label: 'Qualquer regra (OU)' },
];

export const BASE_CONDITION_FIELDS = [
  { value: 'contact.name', label: 'Contato · Nome' },
  { value: 'contact.phone', label: 'Contato · Telefone' },
  { value: 'contact.email', label: 'Contato · E-mail' },
  { value: 'contact.cpf', label: 'Contato · CPF' },
  { value: 'conversation.labels', label: 'Conversa · Etiquetas' },
  { value: 'conversation.status', label: 'Conversa · Status' },
  { value: 'conversation.inbox_id', label: 'Conversa · Caixa (ID)' },
  { value: 'conversation.priority', label: 'Conversa · Prioridade' },
  { value: 'message.content', label: 'Mensagem atual · Conteúdo' },
];

export const DECISION_BRANCH_LABELS = ['Sim', 'Não'];
export const HTTP_BRANCH_LABELS = ['Sucesso', 'Erro'];

const operatorMeta = value =>
  CONDITION_OPERATORS.find(operator => operator.value === value);

export const operatorNeedsValue = value => operatorMeta(value)?.needsValue;

let ruleSequence = 0;
export const emptyRule = () => {
  ruleSequence += 1;
  return {
    id: `rule-${Date.now()}-${ruleSequence}`,
    field: '',
    operator: 'equals',
    value: '',
  };
};

export const normalizeCondition = condition => ({
  match: condition?.match === 'any' ? 'any' : 'all',
  rules: Array.isArray(condition?.rules)
    ? condition.rules.map(rule => ({ ...emptyRule(), ...rule }))
    : [],
});

export const conditionFieldOptions = ({
  contactAttributes = [],
  httpVariables = [],
} = {}) => {
  const custom = contactAttributes
    .filter(attribute => attribute?.attribute_key)
    .map(attribute => ({
      value: `contact.custom_attributes.${attribute.attribute_key}`,
      label: `Contato · ${attribute.attribute_display_name || attribute.attribute_key}`,
    }));
  const http = httpVariables.map(variable => ({
    value: variable.token,
    label: `${variable.group} · ${variable.label}`,
  }));
  const seen = new Set();
  return [...BASE_CONDITION_FIELDS, ...custom, ...http].filter(option => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

export const activeRules = condition =>
  normalizeCondition(condition).rules.filter(
    rule => String(rule.field || '').trim() && operatorMeta(rule.operator)
  );

export const validateCondition = condition => {
  const { rules } = normalizeCondition(condition);
  if (!rules.length) return { isValid: false, message: 'Adicione uma regra' };
  const incomplete = rules.find(
    rule =>
      !String(rule.field || '').trim() ||
      !operatorMeta(rule.operator) ||
      (operatorNeedsValue(rule.operator) && String(rule.value ?? '') === '')
  );
  if (incomplete) return { isValid: false, message: 'Regra incompleta' };
  return { isValid: true, message: '' };
};

export const conditionSummary = (condition, fieldOptions = []) => {
  const rules = activeRules(condition);
  if (!rules.length) return '';
  const joiner = normalizeCondition(condition).match === 'any' ? ' ou ' : ' e ';
  return `Se ${rules
    .map(rule => {
      const field =
        fieldOptions.find(option => option.value === rule.field)?.label ||
        rule.field;
      const operator = operatorMeta(rule.operator)?.label || rule.operator;
      return operatorNeedsValue(rule.operator)
        ? `${field} ${operator} “${rule.value}”`
        : `${field} ${operator}`;
    })
    .join(joiner)}`;
};

// Rótulos de ramo que o backend reconhece para cada tipo de nó de origem.
export const branchLabelsForNode = node => {
  if (!node) return [];
  if (node.type === 'decision') return DECISION_BRANCH_LABELS;
  const actionType = node.action?.type;
  if (actionType === 'http_request') return HTTP_BRANCH_LABELS;
  if (actionType === 'send_whatsapp_flow') return ['Enviado', 'Não enviado'];
  if (actionType === 'run_credit_engine')
    return ['Elegível', 'Inelegível', 'Dados incompletos'];
  return [];
};

export const suggestBranchLabel = (node, edges = []) => {
  const labels = branchLabelsForNode(node);
  const used = new Set(
    edges.filter(edge => edge.source === node?.id).map(edge => edge.label)
  );
  return labels.find(label => !used.has(label)) || '';
};
